import { useEffect, useRef } from "react"

import type { AiImageSpec } from "@productlane/lib/src/ai"
import {
	splitAiHtmlByImagePlaceholders,
	stripAiImagePlaceholders,
} from "@productlane/lib/src/ai"

import { Html } from "../html"
import { AiAnnotatedImage } from "./annotated-image"

// While the answer streams the body is written imperatively so new words can be
// faded in, which rules out React children. Images land once the turn finishes.
function useStreamingFade(html: string, isStreaming: boolean) {
	const contentRef = useRef<HTMLDivElement>(null)
	const prevTextLengthRef = useRef(0)

	useEffect(() => {
		if (!isStreaming) {
			prevTextLengthRef.current = 0
			return
		}
		if (!contentRef.current || !html) return
		const prevLength = prevTextLengthRef.current
		contentRef.current.innerHTML = html
		const walker = document.createTreeWalker(
			contentRef.current,
			NodeFilter.SHOW_TEXT,
		)
		let cumLength = 0
		const newNodes: Text[] = []
		let node: Node | null
		while ((node = walker.nextNode())) {
			const len = (node.textContent ?? "").length
			if (cumLength >= prevLength && len > 0) newNodes.push(node as Text)
			cumLength += len
		}
		prevTextLengthRef.current = cumLength
		for (const textNode of newNodes) {
			const parent = textNode.parentNode
			if (!parent) continue
			const span = document.createElement("span")
			span.className = "ai-fade-word"
			span.textContent = textNode.textContent
			parent.replaceChild(span, textNode)
		}
	}, [isStreaming, html])

	return contentRef
}

export const AiChatMessageBody = ({
	html,
	images,
	isStreaming = false,
}: {
	html: string
	images?: Record<string, AiImageSpec>
	isStreaming?: boolean
}) => {
	const streamingHtml = stripAiImagePlaceholders(html)
	const contentRef = useStreamingFade(streamingHtml, isStreaming)

	if (isStreaming) return <div ref={contentRef} className="text-wrap"></div>

	const segments = splitAiHtmlByImagePlaceholders(html, images)

	return (
		<>
			{segments.map((segment, index) => {
				if (segment.kind === "html") {
					return <Html key={index} html={segment.html} />
				}
				// A tag can outlive its spec (aborted stream, history replay), and
				// rendering it without one would throw mid-answer.
				const spec = images?.[segment.id]
				return spec ? <AiAnnotatedImage key={index} spec={spec} /> : null
			})}
		</>
	)
}
